import { Field as FinalField, Form } from 'react-final-form';
import { FilledInput, FormControl, FormLabel, Grid, Input, Typography } from '@material-ui/core';
import { css } from '@emotion/react';

import { LoginCredentials } from 'types/userSettings';

import { required } from 'lib/form';
import { i18n } from 'lib/i18n';

import { useColors } from 'hooks/useColors';
import { IColors } from 'types/colors';

import Button from 'components/shared/Button/Button';
import { ResetPasswordLink } from './ResetPasswordLink';
import { ErrorText } from './ErrorText';

const userFormControlCss = css`
	.MuiFormLabel-root {
		padding: 8px 0px;
	}
	&.MuiFormControl-root {
		padding: 10px 0;
	}
`;

const loginInputCss = (colors: IColors) => css`
	background-color: ${colors.bg_background_primary};
	&.MuiInput-root {
		padding: 10px;
		margin: 0px;
	}
	.MuiFilledInput-input {
		height: 40px;
	}
`;

const loginInputFailedCss = (colors: IColors) => css`
	background-color: ${colors.bg_background_primary};
	&.MuiInput-root {
		padding: 10px;
		margin: 0px;
	}
	.MuiFilledInput-input {
		height: 40px;
	}
	border: 2px ${colors.bg_error} solid;
`;

const btnHeightCss = css`
	height: 50px;
`;

const dialogTitleCss = (colors: IColors) => css`
	text-align: center;
	width: 100%;
	color: ${colors.tx_text_primary};
	&.MuiTypography-h5 {
		font-weight: 500;
		font-size: 2.2rem;
	}
`;

interface LoginFormProps {
	onSubmit: (values: LoginCredentials) => void;
	setIsForgotPass: (values: boolean) => void;
	defaulteEmail?: string;
}

const LoginForm = ({ onSubmit, setIsForgotPass, defaulteEmail }: LoginFormProps) => {
	const { colors } = useColors();
	const labels = i18n.loginDialog;

	return (
		<Form
			data-aid="LoginForm"
			onSubmit={onSubmit}
			render={({
				handleSubmit,
				//  form,
				// submitting,
				// pristine,
				// values,
			}) => (
				<form autoComplete="off" onSubmit={handleSubmit}>
					<Typography component="h1" variant="h5" css={dialogTitleCss(colors)} id="login-dialog-title">
						{labels.title}
					</Typography>
					{/* Email Address */}
					<FinalField name="email" validate={required} defaultValue={defaulteEmail} displayEmpty>
						{({ input, meta }) => (
							<FormControl fullWidth css={userFormControlCss}>
								<FormLabel htmlFor="loginEmail">
									{labels.email}
									{'*'}
								</FormLabel>
								<FilledInput
									required
									disableUnderline
									placeholder={labels.email}
									css={meta.error && meta.touched ? loginInputFailedCss(colors) : loginInputCss(colors)}
									autoComplete="email"
									{...input}
								/>
								{meta.error && meta.touched && <ErrorText colors={colors} withMargin={false} error={meta.error} />}
							</FormControl>
						)}
					</FinalField>

					{/* Password */}
					<FinalField name="password" validate={required} displayEmpty>
						{({ input, meta }) => (
							<FormControl fullWidth css={userFormControlCss}>
								<FormLabel htmlFor="loginPassword">
									{labels.password}
									{'*'}
								</FormLabel>
								<Input data-testid="LoginForm-DC15DF" 
									required
									type="password"
									disableUnderline
									placeholder={labels.password}
									css={meta.error && meta.touched ? loginInputFailedCss(colors) : loginInputCss(colors)}
									autoComplete="password"
									{...input}
								/>
								{meta.error && meta.touched && <ErrorText colors={colors} withMargin={false} error={meta.error} />}
							</FormControl>
						)}
					</FinalField>
					<Grid container direction="column" justify="flex-end" alignItems="flex-end">
						<ResetPasswordLink
							colors={colors}
							callback={() => setIsForgotPass(true)}
							labels={labels.forgotPassword}
							withPadding
						/>
					</Grid>
					<Button data-aid="button-LoginForm-login" type="submit" fullWidth variant="contained" color="secondary" css={btnHeightCss}>
						{i18n.auth.loginBtn}
					</Button>
				</form>
			)}
		/>
	);
};

export default LoginForm;
